import { z } from 'zod'
import axios from 'axios'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { PlayerImageSchema } from '../players/api'

async function getNotificationsCount() {
  const response = await axios.get('/api/notifications/unread-count')
  return NotificationsCountSchema.parse(response.data)
}

export function useNotificationsCount() {
  return useSuspenseQuery({
    queryKey: ['notificationsCount'],
    queryFn: getNotificationsCount,
  })
}

async function getNotifications({
  page,
  page_size,
}: {
  page: number
  page_size: number
}) {
  const response = await axios.get('/api/notifications', {
    params: { page, page_size },
  })
  return {
    data: z.array(NotificationSchema).parse(response.data.data),
    totalCount: response.data.total_count,
  }
}

export function useNotifications({
  page,
  page_size,
}: {
  page: number
  page_size: number
}) {
  return useSuspenseQuery({
    queryKey: ['notifications', { page, page_size }],
    queryFn: () => getNotifications({ page, page_size }),
  })
}

export function useNotificationsInfinite() {
  return useInfiniteQuery({
    queryKey: ['notificationsInfinite'],
    queryFn: ({ pageParam }) =>
      getNotifications({ page: pageParam, page_size: 5 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.flatMap((page) => page.data).length
      const totalCount = lastPage.totalCount

      if (loadedCount < totalCount) {
        return allPages.length + 1
      } else {
        return undefined
      }
    },
  })
}

async function markAllAsRead() {
  const response = await axios.post('/api/notifications/read-all')
  return response.data
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notificationsInfinite'] })
      queryClient.invalidateQueries({ queryKey: ['notificationsCount'] })
    },
  })
}

async function markAsRead({ notification_id }: { notification_id: number }) {
  const response = await axios.post(
    `/api/notifications/${notification_id}/read`
  )
  return response.data
}

export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notificationsInfinite'] })
      queryClient.invalidateQueries({ queryKey: ['notificationsCount'] })
    },
  })
}

async function markSeenByEntity({ entities }: { entities: SeenByEntity[] }) {
  const response = await axios.post('/api/notifications/mark-seen-by-entity', {
    entities,
  })
  return response.data
}

export function useMarkSeenByEntity() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markSeenByEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notificationsInfinite'] })
      queryClient.invalidateQueries({ queryKey: ['notificationsCount'] })
      queryClient.invalidateQueries({ queryKey: ['unseenContent'] })
    },
  })
}

async function getUnseenContent() {
  const response = await axios.get('/api/notifications/unseen-content')
  return UnseenContentSchema.parse(response.data)
}

export function useUnseenContent() {
  return useQuery({
    queryKey: ['unseenContent'],
    queryFn: getUnseenContent,
    refetchInterval: 60000,
  })
}

export const BaseNotificationSchema = z.object({
  notification_id: z.number(),
  title: z.string(),
  content: z.string(),
  entity_id: z.number(),
  created_at: z.string(),
  is_read: z.boolean(),
  read_at: z.string().nullable(),
  is_marked_all: z.boolean(),
})

const PostNotificationSchema = BaseNotificationSchema.extend({
  notification_type: z.literal('post_created'),
  entity_type: z.literal('post'),
  metadata: z.object({
    post_id: z.number(),
    competition_id: z.number(),
    action: z.string(),
    author_user_id: z.number(),
    author_name: z.string(),
    author_photo: PlayerImageSchema,
  }),
})

const CommentNotificationSchema = BaseNotificationSchema.extend({
  notification_type: z.enum(['comment_created', 'comment_reply']),
  entity_type: z.literal('comment'),
  metadata: z.object({
    post_id: z.number(),
    comment_id: z.number(),
    competition_id: z.number(),
    action: z.string(),
    author_user_id: z.number(),
    author_name: z.string(),
    author_photo: PlayerImageSchema,
  }),
})

const NewsNotificationchema = BaseNotificationSchema.extend({
  notification_type: z.literal('news_published'),
  entity_type: z.literal('news'),
  metadata: z.object({
    action: z.string(),
    news_id: z.number().optional(),
  }),
})

export const NotificationSchema = z.discriminatedUnion('entity_type', [
  PostNotificationSchema,
  CommentNotificationSchema,
  NewsNotificationchema,
])

export const NotificationsCountSchema = z.object({
  count: z.number(),
})

export const UnseenContentSchema = z.object({
  comment_ids: z.array(z.number()),
  news_ids: z.array(z.number()),
  post_ids: z.array(z.number()),
})

export const SeenByEntitySchema = z.object({
  entity_id: z.number(),
  entity_type: z.enum(['post', 'comment', 'news']),
})

export type Notification = z.infer<typeof NotificationSchema>
export type SeenByEntity = z.infer<typeof SeenByEntitySchema>
